import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { API } from '../App';
import { useCluster } from '../context/ClusterContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Badge } from '../components/ui/badge.jsx';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs.jsx';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover.jsx';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command.jsx';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog.jsx';
import {
  Plus, Trash2, ShieldCheck, ShieldOff, Search, Layers, RefreshCw,
  Check, ChevronsUpDown, CircleDashed, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Scheduler-policies admin page (rewrite).
 *
 * Three tabs:
 *   • Workloads — every workload in the cluster with its current opt-in
 *     status; one-click include / exclude / clear actions.
 *   • Rules     — CRUD on the namespace + workload policy rows directly.
 *   • Preview   — resolve a hypothetical (namespace, kind, name) against
 *     the current rule set.
 *
 * Endpoints:
 *   GET    /api/v1/clusters/{cluster_id}/scheduler-policies
 *   POST   /api/v1/clusters/{cluster_id}/scheduler-policies/namespaces
 *   DELETE /api/v1/clusters/{cluster_id}/scheduler-policies/namespaces/{namespace}
 *   POST   /api/v1/clusters/{cluster_id}/scheduler-policies/workloads
 *   DELETE /api/v1/clusters/{cluster_id}/scheduler-policies/workloads
 *   GET    /api/v1/clusters/{cluster_id}/scheduler-policies/preview
 *   GET    /api/v1/clusters/{cluster_id}/scheduler-policies/discover/namespaces
 *   GET    /api/v1/clusters/{cluster_id}/scheduler-policies/discover/workloads[?namespace=X]
 */

const WORKLOAD_KINDS = ['Deployment', 'StatefulSet', 'ReplicaSet', 'DaemonSet', 'Job', 'CronJob'];

function policiesUrl(clusterId, suffix = '') {
  return `${API}/v1/clusters/${clusterId}/scheduler-policies${suffix}`;
}

function fmtRelTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return new Date(iso).toLocaleString();
  } catch { return iso; }
}

// ── Client-side resolution — mirrors the agent webhook's resolveOptIn. ─
// Precedence: per-pod annotation > workload rule > namespace rule > default.
// `annotation` is the optiops.ai/scheduler-optin value ('true'|'false'|'') and,
// when set, beats every policy rule — exactly like the agent.
// Returns { opted_in: bool, matched_by: 'pod_include'|'pod_exclude'
//          |'workload_exclude'|'workload_include'|'namespace_include'|'no_match' }
function resolveOptIn(policies, namespace, kind, name, annotation) {
  const a = (annotation || '').trim().toLowerCase();
  if (a === 'false') return { opted_in: false, matched_by: 'pod_exclude' };
  if (a === 'true')  return { opted_in: true,  matched_by: 'pod_include' };

  const wl = (policies?.workload || []).find(
    (r) => r.namespace === namespace && r.owner_kind === kind && r.owner_name === name,
  );
  if (wl) {
    if (wl.mode === 'exclude') return { opted_in: false, matched_by: 'workload_exclude' };
    if (wl.mode === 'include') return { opted_in: true,  matched_by: 'workload_include' };
  }
  const ns = (policies?.namespace || []).find((r) => r.namespace === namespace && r.mode === 'include');
  if (ns) return { opted_in: true, matched_by: 'namespace_include' };
  return { opted_in: false, matched_by: 'no_match' };
}

// ── Searchable Combobox (Popover + Command) ────────────────────────────
function Combobox({ value, onChange, items, placeholder = 'Pick…', emptyText = 'No matches', disabled = false }) {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => {
    const found = items.find((i) => i.value === value);
    return found ? found.label : '';
  }, [items, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={label ? '' : 'text-muted-foreground'}>{label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(v) => { onChange(v); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${item.value === value ? 'opacity-100' : 'opacity-0'}`} />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Confirm-delete dialog ───────────────────────────────────────────────
function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, destructive = true }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={destructive ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={onConfirm}
          >
            {destructive ? 'Remove' : 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Status badge for a single resolution result ─────────────────────────
function StatusBadge({ result }) {
  const map = {
    pod_include:       { icon: ShieldCheck, label: 'Opted in (pod override)',   cls: 'bg-green-100 text-green-800 border-green-300' },
    pod_exclude:       { icon: ShieldOff,   label: 'Excluded (pod override)',   cls: 'bg-red-100 text-red-800 border-red-300' },
    workload_include:  { icon: ShieldCheck, label: 'Opted in (workload rule)', cls: 'bg-green-100 text-green-800 border-green-300' },
    namespace_include: { icon: ShieldCheck, label: 'Opted in (namespace)',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    workload_exclude:  { icon: ShieldOff,   label: 'Excluded',                  cls: 'bg-red-100 text-red-800 border-red-300' },
    no_match:          { icon: CircleDashed,label: 'Not opted in',              cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  };
  const { icon: Icon, label, cls } = map[result.matched_by] || map.no_match;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────

function SchedulerPolicies() {
  const { selectedCluster, clusters, loading: clusterLoading } = useCluster();
  const clusterId = selectedCluster?.cluster_id || selectedCluster?.id;

  const [policies, setPolicies] = useState({ namespace: [], workload: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Discovery state.
  const [availableNamespaces, setAvailableNamespaces] = useState([]);
  const [allWorkloads, setAllWorkloads] = useState([]); // [{namespace, owner_kind, owner_name}]
  const [snapshotAt, setSnapshotAt] = useState(null);

  // Add-rule drafts.
  const [nsDraft, setNsDraft] = useState('');
  const [wlDraft, setWlDraft] = useState({ namespace: '', owner_kind: 'Deployment', owner_name: '', mode: 'include' });
  const [wlScopedWorkloads, setWlScopedWorkloads] = useState([]);

  // Workload Explorer filters.
  const [explorerNs, setExplorerNs] = useState('');         // '' = all
  const [explorerStatus, setExplorerStatus] = useState(''); // '' = all
  const [explorerSearch, setExplorerSearch] = useState('');

  // Preview state.
  const [previewDraft, setPreviewDraft] = useState({ namespace: '', owner_kind: 'Deployment', owner_name: '', annotation: 'none' });
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewScopedWorkloads, setPreviewScopedWorkloads] = useState([]);

  // Confirm-delete dialog state.
  const [confirm, setConfirm] = useState(null); // { title, description, onConfirm }

  // ── Loaders ─────────────────────────────────────────────────────────

  const loadPolicies = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(policiesUrl(clusterId));
      setPolicies({
        namespace: r.data?.namespace || [],
        workload: r.data?.workload || [],
      });
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  const loadNamespaces = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(policiesUrl(clusterId, '/discover/namespaces'));
      setAvailableNamespaces(r.data?.namespaces || []);
      setSnapshotAt(r.data?.snapshot_at || null);
    } catch { setAvailableNamespaces([]); setSnapshotAt(null); }
  }, [clusterId]);

  const loadAllWorkloads = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(policiesUrl(clusterId, '/discover/workloads'));
      setAllWorkloads(r.data?.workloads || []);
    } catch { setAllWorkloads([]); }
  }, [clusterId]);

  const refreshAll = useCallback(() => {
    loadPolicies();
    loadNamespaces();
    loadAllWorkloads();
  }, [loadPolicies, loadNamespaces, loadAllWorkloads]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Workloads scoped to the wlDraft.namespace.
  useEffect(() => {
    const ns = wlDraft.namespace;
    if (!clusterId || !ns) { setWlScopedWorkloads([]); return; }
    let cancelled = false;
    axios.get(policiesUrl(clusterId, '/discover/workloads'), { params: { namespace: ns } })
      .then((r) => { if (!cancelled) setWlScopedWorkloads(r.data?.workloads || []); })
      .catch(() => { if (!cancelled) setWlScopedWorkloads([]); });
    return () => { cancelled = true; };
  }, [clusterId, wlDraft.namespace]);

  // Workloads scoped to the previewDraft.namespace.
  useEffect(() => {
    const ns = previewDraft.namespace;
    if (!clusterId || !ns) { setPreviewScopedWorkloads([]); return; }
    let cancelled = false;
    axios.get(policiesUrl(clusterId, '/discover/workloads'), { params: { namespace: ns } })
      .then((r) => { if (!cancelled) setPreviewScopedWorkloads(r.data?.workloads || []); })
      .catch(() => { if (!cancelled) setPreviewScopedWorkloads([]); });
    return () => { cancelled = true; };
  }, [clusterId, previewDraft.namespace]);

  // ── Mutations ───────────────────────────────────────────────────────

  const addNamespace = async (ns) => {
    const namespace = (ns ?? nsDraft).trim();
    if (!namespace) return;
    try {
      await axios.post(policiesUrl(clusterId, '/namespaces'), { namespace, mode: 'include' });
      setNsDraft('');
      toast.success(`Namespace "${namespace}" opted in`);
      await loadPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add namespace');
    }
  };

  const removeNamespace = (namespace) => {
    setConfirm({
      title: `Remove namespace "${namespace}" opt-in?`,
      description: 'Workloads in this namespace will no longer be picked up by optops-scheduler. Existing Pending pods stay on optops-scheduler (schedulerName is immutable).',
      onConfirm: async () => {
        try {
          await axios.delete(policiesUrl(clusterId, `/namespaces/${encodeURIComponent(namespace)}`));
          toast.success(`Namespace "${namespace}" removed`);
          await loadPolicies();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Failed to delete namespace');
        }
        setConfirm(null);
      },
    });
  };

  const upsertWorkloadRule = async ({ namespace, owner_kind, owner_name, mode }) => {
    if (!namespace.trim() || !owner_name.trim()) {
      toast.error('Namespace and workload name are required');
      return;
    }
    try {
      await axios.post(policiesUrl(clusterId, '/workloads'), {
        namespace: namespace.trim(),
        owner_kind,
        owner_name: owner_name.trim(),
        mode,
      });
      toast.success(`${mode === 'include' ? 'Included' : 'Excluded'} ${owner_kind}/${owner_name.trim()}`);
      await loadPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save workload rule');
    }
  };

  const addWorkloadFromDraft = async () => {
    await upsertWorkloadRule(wlDraft);
    setWlDraft({ namespace: wlDraft.namespace, owner_kind: 'Deployment', owner_name: '', mode: 'include' });
  };

  const removeWorkloadRule = (row, opts = {}) => {
    const doDelete = async () => {
      try {
        await axios.delete(policiesUrl(clusterId, '/workloads'), {
          data: { namespace: row.namespace, owner_kind: row.owner_kind, owner_name: row.owner_name },
        });
        toast.success('Rule removed');
        await loadPolicies();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to delete rule');
      }
      setConfirm(null);
    };
    if (opts.silent) return doDelete();
    setConfirm({
      title: `Remove rule for ${row.owner_kind}/${row.owner_name}?`,
      description: `Namespace ${row.namespace}. The workload will fall back to the namespace-level policy (or default-deny).`,
      onConfirm: doDelete,
    });
  };

  const runPreview = async () => {
    const { namespace, owner_kind, owner_name } = previewDraft;
    if (!namespace.trim() || !owner_name.trim()) {
      toast.error('Namespace and workload name are required');
      return;
    }
    setPreviewing(true);
    try {
      const r = await axios.get(policiesUrl(clusterId, '/preview'), {
        params: { namespace: namespace.trim(), owner_kind, owner_name: owner_name.trim() },
      });
      setPreviewResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  // ── Derived stats / explorer rows ───────────────────────────────────

  const stats = useMemo(() => {
    const namespaces = policies.namespace.length;
    const wIncl = policies.workload.filter((w) => w.mode === 'include').length;
    const wExcl = policies.workload.filter((w) => w.mode === 'exclude').length;
    const affected = allWorkloads.filter((w) => resolveOptIn(policies, w.namespace, w.owner_kind, w.owner_name).opted_in).length;
    return { namespaces, wIncl, wExcl, affected, total: allWorkloads.length };
  }, [policies, allWorkloads]);

  const explorerRows = useMemo(() => {
    const search = explorerSearch.trim().toLowerCase();
    return allWorkloads
      .filter((w) => !explorerNs || w.namespace === explorerNs)
      .map((w) => ({ ...w, resolution: resolveOptIn(policies, w.namespace, w.owner_kind, w.owner_name) }))
      .filter((w) => !explorerStatus || w.resolution.matched_by === explorerStatus)
      .filter((w) => !search ||
        w.namespace.toLowerCase().includes(search) ||
        w.owner_name.toLowerCase().includes(search) ||
        w.owner_kind.toLowerCase().includes(search));
  }, [allWorkloads, policies, explorerNs, explorerStatus, explorerSearch]);

  // Quick action helpers for the Workload Explorer rows.
  const includeWorkload = (w) => upsertWorkloadRule({ namespace: w.namespace, owner_kind: w.owner_kind, owner_name: w.owner_name, mode: 'include' });
  const excludeWorkload = (w) => upsertWorkloadRule({ namespace: w.namespace, owner_kind: w.owner_kind, owner_name: w.owner_name, mode: 'exclude' });
  const clearWorkloadRule = (w) => removeWorkloadRule({ namespace: w.namespace, owner_kind: w.owner_kind, owner_name: w.owner_name }, { silent: true });

  const hasWorkloadRule = (w) => policies.workload.some(
    (r) => r.namespace === w.namespace && r.owner_kind === w.owner_kind && r.owner_name === w.owner_name,
  );

  // ── Render ──────────────────────────────────────────────────────────

  if (clusterLoading) return <Layout><div className="p-6">Loading…</div></Layout>;
  if (!clusters || clusters.length === 0) {
    return (
      <Layout>
        <div className="p-6">
          <Card><CardContent className="p-6">No clusters connected. Add a cluster from the Clusters page first.</CardContent></Card>
        </div>
      </Layout>
    );
  }

  const namespaceItems = availableNamespaces.map((ns) => ({ value: ns, label: ns }));
  const wlScopedItems = wlScopedWorkloads.map((w) => ({ value: `${w.owner_kind}::${w.owner_name}`, label: `${w.owner_kind}/${w.owner_name}` }));
  const previewScopedItems = previewScopedWorkloads.map((w) => ({ value: `${w.owner_kind}::${w.owner_name}`, label: `${w.owner_kind}/${w.owner_name}` }));

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Layers className="w-6 h-6" /> Scheduler Policies
            </h1>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Decide which workloads the optops scheduler picks up. Precedence: a per-pod annotation
              beats a workload rule, which beats a namespace rule.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {snapshotAt && (
              <span className="text-xs text-gray-500">
                Snapshot: {fmtRelTime(snapshotAt)}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={refreshAll}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded border border-red-200 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Namespace opt-ins" value={stats.namespaces} accent="emerald" />
          <StatCard label="Workload includes" value={stats.wIncl}     accent="emerald" />
          <StatCard label="Workload excludes" value={stats.wExcl}     accent="red" />
          <StatCard label="Workloads affected" value={`${stats.affected} / ${stats.total}`} accent="blue" sub="opted in vs. observed" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="workloads">
          <TabsList>
            <TabsTrigger value="workloads">Workloads ({stats.total})</TabsTrigger>
            <TabsTrigger value="rules">Rules ({stats.namespaces + stats.wIncl + stats.wExcl})</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          {/* ─── Workloads ─────────────────────────────────────────── */}
          <TabsContent value="workloads" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Workload explorer</CardTitle>
                <div className="text-xs text-gray-500">
                  Every workload reported by the agent — what status each one resolves to right now, and one-click controls to change it.
                </div>
              </CardHeader>
              <CardContent>
                {/* Filter bar */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                  <Select value={explorerNs || 'all'} onValueChange={(v) => setExplorerNs(v === 'all' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="All namespaces" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All namespaces</SelectItem>
                      {availableNamespaces.map((ns) => <SelectItem key={ns} value={ns}>{ns}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={explorerStatus || 'all'} onValueChange={(v) => setExplorerStatus(v === 'all' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="namespace_include">Opted in (namespace)</SelectItem>
                      <SelectItem value="workload_include">Opted in (workload)</SelectItem>
                      <SelectItem value="workload_exclude">Excluded</SelectItem>
                      <SelectItem value="no_match">Not opted in</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="md:col-span-2 relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search by name, kind, or namespace…"
                      value={explorerSearch}
                      onChange={(e) => setExplorerSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>

                {loading || allWorkloads.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">
                    {loading ? 'Loading…' : 'No workloads detected yet. Once the agent reports a snapshot they\'ll appear here.'}
                  </div>
                ) : explorerRows.length === 0 ? (
                  <div className="text-sm text-gray-500 py-6 text-center">No workloads match the current filter.</div>
                ) : (
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Namespace</th>
                          <th className="text-left px-3 py-2 font-medium">Workload</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {explorerRows.map((w) => {
                          const ruleExists = hasWorkloadRule(w);
                          return (
                            <tr key={`${w.namespace}/${w.owner_kind}/${w.owner_name}`} className="border-t hover:bg-gray-50 dark:hover:bg-gray-900">
                              <td className="px-3 py-2 font-mono text-xs">{w.namespace}</td>
                              <td className="px-3 py-2"><span className="text-gray-500">{w.owner_kind}/</span><span className="font-medium">{w.owner_name}</span></td>
                              <td className="px-3 py-2"><StatusBadge result={w.resolution} /></td>
                              <td className="px-3 py-2 text-right">
                                <div className="inline-flex items-center gap-1">
                                  {w.resolution.matched_by !== 'workload_include' && (
                                    <Button size="sm" variant="outline" onClick={() => includeWorkload(w)} title="Force-include">
                                      <ShieldCheck className="w-3 h-3 mr-1 text-green-600" /> Include
                                    </Button>
                                  )}
                                  {w.resolution.matched_by !== 'workload_exclude' && (
                                    <Button size="sm" variant="outline" onClick={() => excludeWorkload(w)} title="Force-exclude">
                                      <ShieldOff className="w-3 h-3 mr-1 text-red-600" /> Exclude
                                    </Button>
                                  )}
                                  {ruleExists && (
                                    <Button size="sm" variant="ghost" onClick={() => clearWorkloadRule(w)} title="Clear workload-level rule (falls back to namespace)">
                                      Clear
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  Showing {explorerRows.length} of {allWorkloads.length} workloads
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Rules ─────────────────────────────────────────────── */}
          <TabsContent value="rules" className="mt-4 space-y-4">

            {/* Namespace rules */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Namespace opt-ins</CardTitle>
                <div className="text-xs text-gray-500">
                  Every pod in these namespaces is eligible for the optops scheduler (subject to the webhook's complex-constraint pre-screen).
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 mb-4">
                  <div className="flex-1">
                    <Label className="text-xs">Namespace</Label>
                    {availableNamespaces.length > 0 ? (
                      <Combobox value={nsDraft} onChange={setNsDraft} items={namespaceItems} placeholder="Pick a namespace…" />
                    ) : (
                      <Input
                        placeholder="No snapshot yet — type the namespace"
                        value={nsDraft}
                        onChange={(e) => setNsDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addNamespace(); }}
                      />
                    )}
                  </div>
                  <Button onClick={() => addNamespace()} disabled={!nsDraft.trim()}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {loading ? <div className="text-sm text-gray-500">Loading…</div>
                  : policies.namespace.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4 text-center border rounded border-dashed">
                      No namespace opt-ins yet. Pick a namespace above to opt in every workload inside it.
                    </div>
                  ) : (
                    <RulesTable
                      rows={policies.namespace}
                      columns={[
                        { header: 'Namespace', render: (r) => <span className="font-mono">{r.namespace}</span> },
                        { header: 'Updated',   render: (r) => <span className="text-gray-500 text-xs">{fmtRelTime(r.updated_at)}</span> },
                        { header: 'By',        render: (r) => <span className="text-gray-500 text-xs">{r.updated_by || '—'}</span> },
                      ]}
                      onRemove={(r) => removeNamespace(r.namespace)}
                      removeTitle="Remove namespace opt-in"
                    />
                  )}
              </CardContent>
            </Card>

            {/* Workload rules */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Workload rules</CardTitle>
                <div className="text-xs text-gray-500">
                  Precise opt-ins and exclusions. Exclusions override any namespace opt-in covering the same workload.
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4 items-end">
                  <div>
                    <Label className="text-xs">Namespace</Label>
                    {availableNamespaces.length > 0 ? (
                      <Combobox
                        value={wlDraft.namespace}
                        onChange={(v) => setWlDraft({ ...wlDraft, namespace: v, owner_kind: 'Deployment', owner_name: '' })}
                        items={namespaceItems}
                        placeholder="Pick namespace"
                      />
                    ) : (
                      <Input placeholder="namespace" value={wlDraft.namespace} onChange={(e) => setWlDraft({ ...wlDraft, namespace: e.target.value })} />
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">
                      Workload
                      {wlDraft.namespace && wlScopedWorkloads.length > 0 && (
                        <span className="text-gray-400 ml-2">({wlScopedWorkloads.length})</span>
                      )}
                    </Label>
                    {wlScopedItems.length > 0 ? (
                      <Combobox
                        value={wlDraft.owner_kind && wlDraft.owner_name ? `${wlDraft.owner_kind}::${wlDraft.owner_name}` : ''}
                        onChange={(v) => { const [k, n] = v.split('::'); setWlDraft({ ...wlDraft, owner_kind: k, owner_name: n }); }}
                        items={wlScopedItems}
                        placeholder={wlDraft.namespace ? 'Pick workload' : 'Pick namespace first'}
                        disabled={!wlDraft.namespace}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={wlDraft.owner_kind} onValueChange={(v) => setWlDraft({ ...wlDraft, owner_kind: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{WORKLOAD_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="workload name" value={wlDraft.owner_name} onChange={(e) => setWlDraft({ ...wlDraft, owner_name: e.target.value })} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Mode</Label>
                    <Select value={wlDraft.mode} onValueChange={(v) => setWlDraft({ ...wlDraft, mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="include">Include</SelectItem>
                        <SelectItem value="exclude">Exclude</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addWorkloadFromDraft} disabled={!wlDraft.namespace || !wlDraft.owner_name}>
                    <Plus className="w-4 h-4 mr-1" /> Add rule
                  </Button>
                </div>

                {loading ? <div className="text-sm text-gray-500">Loading…</div>
                  : policies.workload.length === 0 ? (
                    <div className="text-sm text-gray-500 py-4 text-center border rounded border-dashed">
                      No workload rules yet. Use the form above for per-workload includes/excludes — or just go to the Workloads tab for one-click controls.
                    </div>
                  ) : (
                    <RulesTable
                      rows={policies.workload}
                      keyOf={(r) => `${r.namespace}/${r.owner_kind}/${r.owner_name}`}
                      columns={[
                        { header: 'Namespace', render: (r) => <span className="font-mono text-xs">{r.namespace}</span> },
                        { header: 'Kind',      render: (r) => r.owner_kind },
                        { header: 'Name',      render: (r) => <span className="font-mono">{r.owner_name}</span> },
                        { header: 'Mode',      render: (r) => (
                          <Badge variant={r.mode === 'exclude' ? 'outline' : 'default'} className="gap-1">
                            {r.mode === 'exclude'
                              ? (<><ShieldOff className="w-3 h-3" /> exclude</>)
                              : (<><ShieldCheck className="w-3 h-3" /> include</>)
                            }
                          </Badge>
                        )},
                        { header: 'Updated',   render: (r) => <span className="text-gray-500 text-xs">{fmtRelTime(r.updated_at)}</span> },
                      ]}
                      onRemove={(r) => removeWorkloadRule(r)}
                      removeTitle="Remove workload rule"
                    />
                  )}
              </CardContent>
            </Card>

            {/* Per-pod override — highest precedence, set on the pod itself. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-green-600" /> Per-pod override (highest priority)
                </CardTitle>
                <div className="text-xs text-gray-500">
                  To opt a single pod in or out regardless of its namespace/workload rule, set this
                  annotation on the pod (or its workload's pod template). It beats every rule above —
                  but still respects scheduler health and the affinity/stateful safety checks. Pod names
                  are ephemeral, so this lives on the pod, not as a rule row here.
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto">{`metadata:
  annotations:
    optiops.ai/scheduler-optin: "true"   # force this pod ONTO optops
    # optiops.ai/scheduler-optin: "false" # force it to kube-scheduler`}</pre>
                <div className="text-xs text-gray-500">
                  Try it in the <span className="font-medium">Preview</span> tab — pick a workload and set
                  the pod annotation to see how it resolves.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── Preview ───────────────────────────────────────────── */}
          <TabsContent value="preview" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="w-5 h-5" /> Resolution preview
                </CardTitle>
                <div className="text-xs text-gray-500">
                  Check whether a specific workload would be opted in. Uses the same resolver as the agent webhook and bin-pack engine.
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Namespace</Label>
                    {availableNamespaces.length > 0 ? (
                      <Combobox
                        value={previewDraft.namespace}
                        onChange={(v) => setPreviewDraft({ ...previewDraft, namespace: v, owner_kind: 'Deployment', owner_name: '' })}
                        items={namespaceItems}
                        placeholder="Pick namespace"
                      />
                    ) : (
                      <Input placeholder="production" value={previewDraft.namespace} onChange={(e) => setPreviewDraft({ ...previewDraft, namespace: e.target.value })} />
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Workload</Label>
                    {previewScopedItems.length > 0 ? (
                      <Combobox
                        value={previewDraft.owner_kind && previewDraft.owner_name ? `${previewDraft.owner_kind}::${previewDraft.owner_name}` : ''}
                        onChange={(v) => { const [k, n] = v.split('::'); setPreviewDraft({ ...previewDraft, owner_kind: k, owner_name: n }); }}
                        items={previewScopedItems}
                        placeholder={previewDraft.namespace ? 'Pick workload' : 'Pick namespace first'}
                        disabled={!previewDraft.namespace}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={previewDraft.owner_kind} onValueChange={(v) => setPreviewDraft({ ...previewDraft, owner_kind: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{WORKLOAD_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="workload name" value={previewDraft.owner_name} onChange={(e) => setPreviewDraft({ ...previewDraft, owner_name: e.target.value })} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Pod annotation</Label>
                    <Select value={previewDraft.annotation} onValueChange={(v) => setPreviewDraft({ ...previewDraft, annotation: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">none</SelectItem>
                        <SelectItem value="true">scheduler-optin: true</SelectItem>
                        <SelectItem value="false">scheduler-optin: false</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={runPreview} disabled={previewing || !previewDraft.namespace || !previewDraft.owner_name}>
                    {previewing ? 'Resolving…' : 'Resolve'}
                  </Button>
                </div>

                {previewResult && (() => {
                  // Per-pod annotation beats the namespace/workload rule (mirrors
                  // the agent). When chosen, it overrides the backend resolution.
                  const override =
                    previewDraft.annotation === 'true'
                      ? { opted_in: true, matched_by: { granularity: 'pod', mode: 'include' } }
                      : previewDraft.annotation === 'false'
                        ? { opted_in: false, matched_by: { granularity: 'pod', mode: 'exclude' } }
                        : null;
                  const shown = override || previewResult;
                  return (
                    <div className="mt-4 p-4 rounded border bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center gap-2 mb-2">
                        {shown.opted_in
                          ? <Badge className="gap-1"><ShieldCheck className="w-3 h-3" /> opted in</Badge>
                          : <Badge variant="outline" className="gap-1"><ShieldOff className="w-3 h-3" /> not opted in</Badge>
                        }
                      </div>
                      {shown.matched_by ? (
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          Matched by <span className="font-mono">{shown.matched_by.granularity}</span> rule
                          {(shown.matched_by.granularity === 'workload' || shown.matched_by.granularity === 'pod') && (<> with mode <span className="font-mono">{shown.matched_by.mode}</span></>)}.
                          {override && (
                            <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              The per-pod annotation overrides the namespace/workload rule.
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          No matching rule — fell through to default-deny.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={confirm != null}
        onOpenChange={(open) => { if (!open) setConfirm(null); }}
        title={confirm?.title || ''}
        description={confirm?.description || ''}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
    </Layout>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Small presentational components
// ────────────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  const color = {
    emerald: 'text-emerald-600',
    red:     'text-red-600',
    blue:    'text-blue-600',
    gray:    'text-gray-600',
  }[accent] || 'text-gray-600';
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className={`text-2xl font-semibold ${color}`}>{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function RulesTable({ rows, columns, onRemove, removeTitle, keyOf }) {
  const k = keyOf || ((r) => r.namespace);
  return (
    <div className="border rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((c, i) => <th key={i} className="text-left px-3 py-2 font-medium">{c.header}</th>)}
            <th className="w-12" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={k(r)} className="border-t hover:bg-gray-50 dark:hover:bg-gray-900">
              {columns.map((c, i) => <td key={i} className="px-3 py-2">{c.render(r)}</td>)}
              <td className="px-3 py-2">
                <Button size="icon" variant="ghost" onClick={() => onRemove(r)} title={removeTitle}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SchedulerPolicies;
